import { CircleDashedIcon, Icon as PhosphorIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import SpinAnimation from "@gx/dls/animations/SpinAnimation";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import Spacing from "@gx/dls/containers/Spacing";
import Icon, { IconVariant } from "@gx/dls/icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";

export enum LoadingLayoutSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

interface LoadingLayoutProps {
  size?: LoadingLayoutSize;
  icon?: PhosphorIcon;
  title?: string;
  description?: string;
  fillHeight?: boolean;
}

const LoadingLayout = ({
  size = LoadingLayoutSize.MEDIUM,
  icon = CircleDashedIcon,
  title,
  description,
  fillHeight = true,
}: LoadingLayoutProps) => {
  return match(size)
    .with(LoadingLayoutSize.SMALL, () => (
      <FlexWrapper
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        fillWidth
        fillHeight={fillHeight}
      >
        <Spacing bottom={8}>
          <SpinAnimation>
            <Icon component={icon} variant={IconVariant.TERTIARY} size={16} />
          </SpinAnimation>
        </Spacing>
        {title && <Text weight={TextWeight.MEDIUM}>{title}</Text>}
        {description && (
          <>
            <Spacing top={6} />
            <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_SM}>
              {description}
            </Text>
          </>
        )}
      </FlexWrapper>
    ))
    .with(LoadingLayoutSize.MEDIUM, () => (
      <FlexWrapper
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        overflow="hidden"
        fillWidth
        fillHeight={fillHeight}
      >
        <Spacing bottom={12}>
          <SpinAnimation>
            <Icon component={icon} variant={IconVariant.TERTIARY} size={20} />
          </SpinAnimation>
        </Spacing>
        {title && <Text size={TextSize.BODY_LG}>{title}</Text>}
        {description && (
          <>
            <Spacing top={8} />
            <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_MD}>
              {description}
            </Text>
          </>
        )}
      </FlexWrapper>
    ))
    .with(LoadingLayoutSize.LARGE, () => (
      <FlexWrapper
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        fillWidth
        fillHeight={fillHeight}
      >
        <Spacing bottom={12}>
          <SpinAnimation>
            <Icon component={icon} variant={IconVariant.TERTIARY} size={24} />
          </SpinAnimation>
        </Spacing>
        {title && <Text size={TextSize.HEADING_MD}>{title}</Text>}
        {description && (
          <>
            <Spacing top={16} />
            <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_LG}>
              {description}
            </Text>
          </>
        )}
      </FlexWrapper>
    ))
    .exhaustive();
};

export default LoadingLayout;
